import { compactNumber } from "@/lib/formatNumber";
import { avatarSrc } from "@/lib/defaults";
import { renderMentions, mentionQuery, MENTION_TRIGGER } from "@/components/MentionText";
import { renderRichText, renderLinks } from "@/components/RichText";
import { LinkCard, useLinkPreview } from "@/components/LinkCard";
import { parseFirstUrl } from "@/lib/embeds";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { emitPostPatch } from "@/lib/postSync";
import { Link, useNavigate } from "react-router-dom";
import { api, getToken } from "@/lib/api";
import { isOffline } from "@/lib/netStatus";
import { EmojiTabsGrid } from "@/components/EmojiTabsGrid";
import { enqueueOffline, shouldQueue, EVT_FLUSHED, EVT_FAILED } from "@/lib/offlineQueue";
import { useApp } from "@/context/AppContext";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { VerifiedBadge } from "@/components/VerifiedBadge";
import { resolveMediaUrl } from "@/components/FileUpload";
import { PostAudioPlayer } from "@/components/PostAudioPlayer";
import { localizeReference } from "@/lib/bibleBooks";
import { Send, Trash2, X, Loader2, MessageCircle, Smile, Pin, Star, WifiOff } from "lucide-react";
import { toast } from "sonner";
import { ReportDialog } from "@/components/ReportDialog";
import { PostPhotos } from "@/components/PhotoGallery";
import { FeedVideo } from "@/components/FeedVideo";
import { EmbedPlayer } from "@/components/EmbedPlayer";
import { parseVideoEmbed } from "@/lib/embeds";
import { stripEmbedUrl } from "@/lib/linkText";
import { bgStyle } from "@/lib/gradients";
import { AutoFitText } from "@/components/AutoFitText";
import { BgTextFrame, BG_TEXT_PAD, BG_TEXT_SHADOW, BG_TEXT_MAX_PX } from "@/components/BgTextFrame";
import { ReactionBar } from "@/components/ReactionBar";
import { ReactorsDialog } from "@/components/ReactorsDialog";
import { timeAgo } from "@/lib/timeAgo";
import { keepFocusTap } from "@/lib/keepFocusTap";

const WS_BASE = process.env.REACT_APP_BACKEND_URL.replace(/^http/, "ws");
const EMOJI = { love: "👍", adore: "❤️", amen: "🙏", gloire: "🙌", sad: "😢", rire: "😂", wow: "😮" };

// Lot 667 : liens cliquables (libellé court) + carte d'aperçu riche sous le commentaire.
const renderContent = (content, mentions) => renderLinks(String(content || ""), (leaf) => renderMentions(leaf, mentions), "c");

// Facebook-style: render the FULL post (author, text/bg, verse, media — images,
// video/replay HLS, article/embed, audio) at the top of the comments modal.
const PostFull = ({ post, onComment }) => {
  const { t, lang } = useApp();
  const nav = useNavigate();
  const author = post.author || {};
  const content = post.content || "";
  const storedEmbed = (post.embed && (post.embed.src || post.embed.kind === "article")) ? post.embed : parseVideoEmbed(content);
  // Lot 667 : sans carte enregistrée, l'aperçu du lien (Facebook, article…) est récupéré à l'affichage.
  const lazyEmbed = useLinkPreview((!storedEmbed && !post.embed_dismissed && !post.images?.length && !post.video_url && !post.verse) ? parseFirstUrl(content) : null);
  const embed = storedEmbed || (lazyEmbed?.kind === "article" ? lazyEmbed : null);
  // Carte de lien affichée sous le texte → l'URL brute n'est pas répétée.
  const shownText = (!post.images?.length && !post.video_url && embed) ? stripEmbedUrl(content, embed) : content;
  return (
    <div className="px-4 pt-3" data-testid={`comment-post-full-${post.id}`}>
      <header className="flex items-start gap-3">
        <Link to={author.is_church && author.church_id ? `/churches/${author.church_id}` : `/profile/${author.id}`}>
          <Avatar className="h-10 w-10"><AvatarImage src={avatarSrc(author)} /><AvatarFallback>{author.name?.[0]}</AvatarFallback></Avatar>
        </Link>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <Link to={author.is_church && author.church_id ? `/churches/${author.church_id}` : `/profile/${author.id}`} className="font-semibold text-sm hover:underline">{author.name}</Link>
            <VerifiedBadge user={author} size={13} />
            {post.is_replay && (
              <span className="chip text-[10px]" style={{ background: "var(--yalola-primary-soft)", color: "var(--yalola-primary)" }}>{t("post.replay_alt", "Rediffusion")}</span>
            )}
          </div>
          <div className="text-xs" style={{ color: "var(--yalola-text-muted)" }}>{timeAgo(post.created_at, t)}{post.edited_at ? ` · ${t("post.edited", "modifié")}` : ""}</div>
        </div>
      </header>

      {shownText && post.bg && bgStyle(post.bg) && !post.images?.length && !post.video_url && !post.audio_url ? (
        <BgTextFrame bg={post.bg} className="mt-3" data-testid={`comment-post-bg-${post.id}`}>
          <AutoFitText text={shownText} scale={post.text_scale || 1} maxPx={BG_TEXT_MAX_PX} boxClassName={BG_TEXT_PAD} className="font-bold text-white" style={BG_TEXT_SHADOW} />
        </BgTextFrame>
      ) : shownText ? (
        <div className="mt-3 text-[15px] leading-relaxed break-words" data-testid={`post-text-${post.id}`}>{renderRichText(shownText, (leaf) => renderMentions(leaf, post.mentions))}</div>
      ) : null}

      {post.verse && (
        <div
          className="mt-3 rounded-2xl p-6 relative overflow-hidden"
          style={post.verse.bg && post.verse.bg.startsWith("/verse-bg/")
            ? { backgroundImage: `url(${post.verse.bg})`, backgroundSize: "cover", backgroundPosition: "center" }
            : { background: "var(--gradient-navy)" }}
          data-testid={`comment-post-verse-${post.id}`}
        >
          {post.verse.bg && post.verse.bg.startsWith("/verse-bg/") && <div className="absolute inset-0 bg-black/35" aria-hidden />}
          <div className="relative">
            <div className="text-[10px] font-bold uppercase tracking-widest" style={{ color: "var(--yalola-accent)" }}>{t("verse.title", "Verset du jour")}</div>
            <p className="serif-italic mt-2 text-lg leading-relaxed text-white/95" style={{ textShadow: "0 1px 3px rgba(0,0,0,0.35)" }}>« {post.verse[`text_${lang}`] || post.verse.text_fr || post.verse.text} »</p>
            <div className="mt-3 text-sm font-semibold" style={{ color: "var(--yalola-accent)" }}>— {localizeReference(post.verse.reference, lang)}</div>
          </div>
        </div>
      )}

      {post.images?.length > 0 && <PostPhotos images={post.images} onOpen={(i) => nav(`/post/${post.id}?photo=${i}`)} />}
      {post.video_url && <FeedVideo src={post.video_url} width={post.video_width || 0} height={post.video_height || 0} testId={`comment-post-video-${post.id}`} fx={post.video_fx || null} onExpand={() => nav(`/post/${post.id}`)} />}
      {!post.images?.length && !post.video_url && embed && (
        embed.kind === "article" ? (
          <a href={embed.url} target="_blank" rel="noreferrer" className="mt-3 block rounded-2xl overflow-hidden border" style={{ borderColor: "var(--yalola-border)" }} data-testid={`comment-post-article-${post.id}`}>
            {embed.thumbnail && <img src={resolveMediaUrl(embed.thumbnail)} loading="lazy" decoding="async" alt="" className="w-full max-h-[360px] object-cover" />}
            <div className="px-4 py-3" style={{ background: "var(--yalola-surface-alt)" }}>
              <div className="text-[10px] font-semibold uppercase tracking-wide" style={{ color: "var(--yalola-text-muted)" }}>{embed.domain}</div>
              {embed.title && <div className="text-sm font-bold leading-snug">{embed.title}</div>}
              {embed.description && <div className="text-xs mt-0.5 line-clamp-2" style={{ color: "var(--yalola-text-muted)" }}>{embed.description}</div>}
            </div>
          </a>
        ) : (
          <div className="mt-1">
            <EmbedPlayer embed={embed} testId={`comment-post-embed-${post.id}`} />
            {(post.embed?.title || post.embed?.author) && (
              <div className="mt-1 rounded-xl border px-3 py-2" style={{ borderColor: "var(--yalola-border)", background: "var(--yalola-surface-alt)" }}>
                <div className="text-[10px] font-semibold uppercase tracking-wide" style={{ color: "var(--yalola-text-muted)" }}>{embed.provider === "youtube" ? "YOUTUBE.COM" : "FACEBOOK.COM"}</div>
                {post.embed?.title && <div className="text-sm font-semibold truncate">{post.embed.title}</div>}
                {post.embed?.author && <div className="text-xs truncate" style={{ color: "var(--yalola-text-muted)" }}>{post.embed.author}</div>}
              </div>
            )}
          </div>
        )
      )}
      {post.audio_url && (
        <div className="mt-3" data-testid={`comment-post-audio-${post.id}`}>
          <PostAudioPlayer post={post} author={author} testId={`comment-post-audio-player-${post.id}`} />
        </div>
      )}

      {/* Facebook-style reaction/comment/share bar inside the modal */}
      <ReactionBar post={post} commentCount={post.comment_count} onCommentClick={onComment} />
    </div>
  );
};

const reactTotal = (c) => Object.values(c?.reactions || {}).reduce((a, b) => a + (b || 0), 0);

const CommentRow = ({ c, isReply, replyCount = 0, expanded = false, t, lang, user, reactions, onReact, onReply, onEdit, onDelete, onToggleReplies, onPin, onReport, isPostOwner, isBest, onOpenReactors, pulseId }) => {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState(c.content);
  const rl = (r) => (r ? r[`label_${lang}`] || r.label_fr || r.key : "");
  const current = reactions.find((r) => r.key === c.my_reaction);
  const total = Object.values(c.reactions || {}).reduce((a, b) => a + b, 0);
  const topEmojis = reactions.filter((r) => (c.reactions?.[r.key] || 0) > 0).slice(0, 3);
  const canEdit = !c.pending && user && user.id === c.author?.id;
  const canDelete = !c.pending && user && (user.id === c.author?.id || user.role === "super_admin" || user.role === "moderator");
  const pick = (key) => { setPickerOpen(false); onReact(c, key); };
  const saveEdit = () => { const v = editText.trim(); if (!v) return; onEdit(c, v); setEditing(false); };

  return (
    <div className={`flex gap-2 items-start ${isReply ? "ml-10 mt-3" : ""} ${pulseId === c.id ? "yalola-attn-pulse" : ""}`} data-comment-id={c.id} data-testid={`comment-row-${c.id}`}>
      <Link to={`/profile/${c.author?.id}`}>
        <Avatar className={isReply ? "h-7 w-7" : "h-8 w-8"}>
          <AvatarImage src={avatarSrc(c.author)} />
          <AvatarFallback>{c.author?.name?.[0]}</AvatarFallback>
        </Avatar>
      </Link>
      <div className="min-w-0 flex-1">
        {c.pinned && (
          <div className="flex items-center gap-1 mb-1 ml-1 text-[11px] font-bold uppercase tracking-wide" style={{ color: "var(--yalola-primary)" }} data-testid={`comment-pinned-badge-${c.id}`}>
            <Pin size={11} className="fill-current" /> {t("comment.pinned", "Épinglé par l'auteur")}
          </div>
        )}
        {isBest && (
          <div className="flex items-center gap-1 mb-1 ml-1 text-[11px] font-bold" style={{ color: "#E0A63C" }} data-testid={`comment-best-reply-${c.id}`}>
            <Star size={11} className="fill-current" /> {t("comment.best_reply", "Meilleure réponse")}
          </div>
        )}

        {editing ? (
          <div className="flex items-center gap-2 mb-1">
            <input
              value={editText}
              onChange={(e) => setEditText(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); saveEdit(); } if (e.key === "Escape") { setEditing(false); setEditText(c.content); } }}
              autoFocus
              className="flex-1 rounded-full border px-3 h-9 text-sm bg-white outline-none"
              style={{ borderColor: "var(--yalola-border)" }}
              data-testid={`comment-edit-input-${c.id}`}
            />
            <button onClick={saveEdit} className="text-xs font-semibold" style={{ color: "var(--yalola-primary)" }} data-testid={`comment-edit-save-${c.id}`}>{t("common.save", "Enregistrer")}</button>
            <button onClick={() => { setEditing(false); setEditText(c.content); }} className="text-xs" style={{ color: "var(--yalola-text-muted)" }} data-testid={`comment-edit-cancel-${c.id}`}>{t("common.cancel", "Annuler")}</button>
          </div>
        ) : (
          <div className="inline-block max-w-full mb-1">
            <div className="rounded-2xl px-3 py-2" style={{ background: "var(--yalola-surface-alt)" }}>
              <div className="flex items-center gap-1.5 flex-wrap">
                <Link to={`/profile/${c.author?.id}`} className="text-xs font-semibold hover:underline inline-flex items-center gap-1">
                  {c.author?.name} <VerifiedBadge user={c.author} size={11} />
                </Link>
                <span className="text-[11px]" style={{ color: "var(--yalola-text-muted)" }} data-testid={`comment-time-${c.id}`}>
                  {timeAgo(c.created_at, t)}{c.edited_at ? ` · ${t("comment.edited", "modifié")}` : ""}
                </span>
                {c.offline && (
                  <span className="text-[11px] inline-flex items-center gap-1 font-medium" style={{ color: "#B45309" }} data-testid={`comment-offline-${c.id}`}>
                    <WifiOff size={11} /> {t("offline.waiting", "En attente du réseau")}
                  </span>
                )}
              </div>
              <div className="text-sm whitespace-pre-wrap break-words">{renderContent(c.content, c.mentions)}</div>
              {parseFirstUrl(c.content) && <LinkCard url={parseFirstUrl(c.content)} testId={`comment-link-${c.id}`} compact />}
            </div>
          </div>
        )}

        {!editing && (
          <div className="flex items-center gap-4 px-1 text-[12px] flex-wrap" style={{ color: "var(--yalola-text-muted)" }}>
            <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
              <PopoverTrigger asChild>
                <button
                  className="font-semibold hover:underline"
                  style={{ color: current ? current.color : "var(--yalola-text-muted)" }}
                  onMouseEnter={() => setPickerOpen(true)}
                  data-testid={`comment-react-btn-${c.id}`}
                >
                  {current ? `${EMOJI[current.key] || "👍"} ${rl(current)}` : t("comment.like", "J'aime")}
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-1.5 rounded-full flex items-center gap-1 z-[95]" align="start" side="top" data-testid={`comment-react-picker-${c.id}`} onMouseLeave={() => setPickerOpen(false)}>
                {reactions.map((r) => (
                  <button
                    key={r.key}
                    onClick={() => pick(r.key)}
                    title={rl(r)}
                    className="text-[26px] leading-none transition-transform hover:scale-125 hover:-translate-y-0.5 rounded-full"
                    style={{ background: c.my_reaction === r.key ? "var(--yalola-primary-soft)" : "transparent", padding: 2 }}
                    data-testid={`comment-react-${r.key}-${c.id}`}
                  >
                    {EMOJI[r.key] || "👍"}
                  </button>
                ))}
              </PopoverContent>
            </Popover>
            <button className="font-semibold hover:underline" onClick={() => onReply(c)} data-testid={`comment-reply-btn-${c.id}`}>
              {t("comment.reply", "Répondre")}
            </button>
            {canEdit && (
              <button className="font-semibold hover:underline" onClick={() => { setEditing(true); setEditText(c.content); }} data-testid={`comment-edit-btn-${c.id}`}>
                {t("comment.edit", "Modifier")}
              </button>
            )}
            {user && user.id !== c.author?.id && (
              <button className="font-semibold hover:underline text-red-600" onClick={() => onReport(c)} data-testid={`comment-report-btn-${c.id}`}>
                {t("post.report", "Signaler")}
              </button>
            )}
            <div className="ml-auto flex items-center gap-3">
              {total > 0 && (
                <button
                  type="button"
                  onClick={() => onOpenReactors?.(c.id)}
                  className="inline-flex items-center gap-0.5 bg-white rounded-full shadow-sm border px-1.5 h-[22px] text-[11px] font-medium hover:brightness-95 transition"
                  style={{ borderColor: "var(--yalola-border)" }}
                  data-testid={`comment-reactions-${c.id}`}
                  title={t("reactions.who_title", "Qui a réagi")}
                >
                  {topEmojis.map((r) => <span key={r.key} className="leading-none text-[13px]">{EMOJI[r.key] || "👍"}</span>)}
                  <span style={{ color: "var(--yalola-text-muted)" }}>{compactNumber(total)}</span>
                </button>
              )}
              {isPostOwner && !isReply && (
                <button
                  className="hover:underline font-semibold inline-flex items-center gap-0.5"
                  style={{ color: c.pinned ? "var(--yalola-primary)" : "var(--yalola-text-muted)" }}
                  onClick={() => onPin(c)}
                  data-testid={`comment-pin-btn-${c.id}`}
                  title={c.pinned ? t("comment.unpin", "Désépingler") : t("comment.pin", "Épingler")}
                >
                  <Pin size={12} className={c.pinned ? "fill-current" : ""} />
                </button>
              )}
              {canDelete && (
                <button className="hover:text-red-600" onClick={() => onDelete(c)} data-testid={`comment-delete-btn-${c.id}`} title={t("comment.delete", "Supprimer")}>
                  <Trash2 size={13} />
                </button>
              )}
            </div>
          </div>
        )}

        {!isReply && replyCount > 0 && (
          <button className="mt-1.5 ml-1 text-[12px] font-semibold hover:underline" style={{ color: "var(--yalola-primary)" }} onClick={() => onToggleReplies(c.id)} data-testid={`comment-toggle-replies-${c.id}`}>
            {expanded ? t("comment.hide_replies", "Masquer les réponses") : t("comment.view_replies", "Voir {count} réponse(s)").replace("{count}", compactNumber(replyCount))}
          </button>
        )}
      </div>
    </div>
  );
};

// Full comment thread (list + replies + reactions + report + who-reacted + mentions
// + realtime). Used both in the pop-up modal and inline on the post-detail/theater page.
export const CommentThread = ({ post, onCountChange, variant = "modal", showPostFull = false, focusCommentId = null, stickyComposer = false }) => {
  const { user, t, lang, config } = useApp();
  const reactions = config?.reactions || [];
  const [comments, setComments] = useState(null);
  const [text, setText] = useState("");
  const [replyTo, setReplyTo] = useState(null);
  const [sending, setSending] = useState(false);
  const [reportComment, setReportComment] = useState(null);
  const [reporting, setReporting] = useState(false);
  const [expandedReplies, setExpandedReplies] = useState({});
  const [reactorsCommentId, setReactorsCommentId] = useState(null);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [typingUsers, setTypingUsers] = useState({});
  const [mentions, setMentions] = useState([]);
  const [mentionResults, setMentionResults] = useState([]);
  const [mentionOpen, setMentionOpen] = useState(false);
  const [pulseId, setPulseId] = useState(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const focusedRef = useRef(false);
  const wsRef = useRef(null);
  const typingTimersRef = useRef({});
  const lastTypingRef = useRef(0);
  const stopTypingRef = useRef(null);
  const mentionReqRef = useRef(0);

  useEffect(() => {
    setComments(null); setReplyTo(null); setText(""); setMentions([]); setTypingUsers({});
    api.get(`/posts/${post.id}/comments`).then(({ data }) => setComments(data.comments || [])).catch(() => setComments([]));
  }, [post.id]);

  // Realtime channel: live comments, reactions, deletes + typing indicators.
  useEffect(() => {
    let closed = false;
    const ws = new WebSocket(`${WS_BASE}/api/ws/post/${post.id}?token=${getToken()}`);
    wsRef.current = ws;
    ws.onmessage = (evt) => {
      let msg;
      try { msg = JSON.parse(evt.data); } catch { return; }
      if (msg.type === "comment") {
        setComments((cs) => (cs && cs.some((x) => x.id === msg.comment.id) ? cs : [...(cs || []), msg.comment]));
      } else if (msg.type === "comment_deleted") {
        setComments((cs) => (cs || []).filter((x) => !msg.ids.includes(x.id)));
      } else if (msg.type === "comment_reaction") {
        setComments((cs) => (cs || []).map((x) => (x.id === msg.comment_id ? { ...x, reactions: msg.reactions } : x)));
      } else if (msg.type === "comment_edited") {
        setComments((cs) => (cs || []).map((x) => (x.id === msg.comment_id ? { ...x, content: msg.content, edited_at: msg.edited_at } : x)));
      } else if (msg.type === "comment_pinned") {
        setComments((cs) => (cs || []).map((x) => ({ ...x, pinned: x.id === msg.comment_id })));
      } else if (msg.type === "typing" && msg.user && msg.user.id !== user?.id) {
        const { id, name } = msg.user;
        setTypingUsers((prev) => {
          const next = { ...prev };
          if (msg.typing) next[id] = name; else delete next[id];
          return next;
        });
        clearTimeout(typingTimersRef.current[id]);
        if (msg.typing) {
          typingTimersRef.current[id] = setTimeout(() => setTypingUsers((p) => { const n = { ...p }; delete n[id]; return n; }), 5000);
        }
      }
    };
    return () => { closed = true; try { ws.close(); } catch { /* noop */ } wsRef.current = null; void closed; };
  }, [post.id, user?.id]);

  // Deep-link from a notification: scroll to + briefly animate the concerned comment.
  useEffect(() => {
    if (!focusCommentId || focusedRef.current || !comments) return;
    const target = comments.find((c) => c.id === focusCommentId);
    if (target?.parent_id) setExpandedReplies((e) => ({ ...e, [target.parent_id]: true }));
    const timer = setTimeout(() => {
      focusedRef.current = true;
      const el = listRef.current?.querySelector(`[data-comment-id="${focusCommentId}"]`);
      if (el) { el.scrollIntoView({ behavior: "smooth", block: "center" }); setPulseId(focusCommentId); setTimeout(() => setPulseId(null), 2200); }
    }, 450);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusCommentId, comments]);

  const sendTyping = () => {
    const ws = wsRef.current;
    if (ws?.readyState !== 1) return;
    const now = Date.now();
    if (now - lastTypingRef.current > 1500) { lastTypingRef.current = now; try { ws.send(JSON.stringify({ type: "typing", typing: true })); } catch { /* noop */ } }
    clearTimeout(stopTypingRef.current);
    stopTypingRef.current = setTimeout(() => { try { ws.send(JSON.stringify({ type: "typing", typing: false })); } catch { /* noop */ } lastTypingRef.current = 0; }, 2500);
  };
  const stopTyping = () => { const ws = wsRef.current; clearTimeout(stopTypingRef.current); lastTypingRef.current = 0; if (ws?.readyState === 1) { try { ws.send(JSON.stringify({ type: "typing", typing: false })); } catch { /* noop */ } } };

  const { topLevel, repliesOf } = useMemo(() => {
    const list = comments || [];
    const top = list.filter((c) => !c.parent_id).sort((a, b) => (b.pinned ? 1 : 0) - (a.pinned ? 1 : 0));
    const map = {};
    list.filter((c) => c.parent_id).forEach((c) => { (map[c.parent_id] = map[c.parent_id] || []).push(c); });
    return { topLevel: top, repliesOf: map };
  }, [comments]);

  const onChangeText = (val) => {
    setText(val);
    sendTyping();
    const q = mentionQuery(val, mentions);
    if (q) {
      const rid = ++mentionReqRef.current;
      api.get(`/search?q=${encodeURIComponent(q)}`).then(({ data }) => {
        if (rid !== mentionReqRef.current) return;
        setMentionResults((data.users || []).slice(0, 5));
        setMentionOpen(true);
      }).catch(() => {});
    } else { setMentionOpen(false); setMentionResults([]); }
  };

  const pickMention = (u) => {
    setText((prev) => prev.replace(MENTION_TRIGGER, `@${u.name} `));
    setMentions((prev) => (prev.some((x) => x.id === u.id) ? prev : [...prev, { id: u.id, name: u.name }]));
    setMentionOpen(false); setMentionResults([]);
    setTimeout(() => inputRef.current?.focus(), 30);
  };

  const send = async () => {
    if (!text.trim() || sending) return;
    setSending(true);
    stopTyping();
    const used = mentions.filter((m) => text.includes(`@${m.name}`));
    const body = { content: text, parent_id: replyTo?.id || null, mentions: used };
    // Lot 630 — hors ligne : bulle provisoire « En attente du réseau », commentaire mis en file (même client_id → pas de doublon).
    const queue = () => {
      const tempId = `tmp-${Date.now()}`;
      enqueueOffline({ kind: "comment", method: "post", url: `/posts/${post.id}/comments`, body, meta: { post_id: post.id, temp_id: tempId } });
      const temp = { id: tempId, author: { id: user?.id, name: user?.name, avatar_url: user?.avatar_url, is_verified: user?.is_verified }, author_id: user?.id,
                     content: body.content, parent_id: body.parent_id, created_at: new Date().toISOString(), reactions: {}, my_reaction: null, mentions: used, pending: true, offline: true };
      setComments((cs) => [...(cs || []), temp]);
      onCountChange?.(1);
      emitPostPatch(post.id, { comment_delta: 1 });
      if (replyTo) setExpandedReplies((e) => ({ ...e, [replyTo.id]: true }));
      setText(""); setReplyTo(null); setMentions([]); setMentionOpen(false);
      setSending(false);
    };
    if (isOffline()) { queue(); return; }
    try {
      const { data } = await api.post(`/posts/${post.id}/comments`, body, { timeout: 45000 });
      setComments((cs) => (cs && cs.some((x) => x.id === data.comment.id) ? cs : [...(cs || []), data.comment]));
      onCountChange?.(1);
      emitPostPatch(post.id, { comment_delta: 1 });
      if (replyTo) setExpandedReplies((e) => ({ ...e, [replyTo.id]: true }));
      setText(""); setReplyTo(null); setMentions([]); setMentionOpen(false);
    } catch (e) {
      if (shouldQueue(e)) { queue(); return; }
      toast.error(e?.friendlyMessage || "Erreur");
    }
    finally { setSending(false); }
  };

  // Lot 630 — la file hors ligne a envoyé (ou vu refuser) un commentaire de CETTE publication → bulle remplacée / retirée.
  useEffect(() => {
    const onFlushed = (e) => {
      const { item, data } = e.detail || {};
      if (item?.kind !== "comment" || item?.meta?.post_id !== post.id || !data?.comment) return;
      setComments((cs) => {
        const base = (cs || []).filter((x) => x.id !== item.meta.temp_id);
        return base.some((x) => x.id === data.comment.id) ? base : [...base, data.comment];
      });
    };
    const onFailed = (e) => {
      const { item } = e.detail || {};
      if (item?.kind !== "comment" || item?.meta?.post_id !== post.id) return;
      setComments((cs) => (cs || []).filter((x) => x.id !== item.meta.temp_id));
      onCountChange?.(-1);
      emitPostPatch(post.id, { comment_delta: -1 });
    };
    window.addEventListener(EVT_FLUSHED, onFlushed);
    window.addEventListener(EVT_FAILED, onFailed);
    return () => { window.removeEventListener(EVT_FLUSHED, onFlushed); window.removeEventListener(EVT_FAILED, onFailed); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [post.id]);

  const react = async (c, key) => {
    if (c.pending) return;
    // Lot 630 — bascule locale immédiate ; hors ligne → mise en file (le serveur bascule à l'identique au retour du réseau).
    const prev = { my_reaction: c.my_reaction, reactions: c.reactions || {} };
    const next = { ...prev.reactions };
    if (prev.my_reaction === key) { next[key] = Math.max(0, (next[key] || 1) - 1); }
    else { next[key] = (next[key] || 0) + 1; if (prev.my_reaction) next[prev.my_reaction] = Math.max(0, (next[prev.my_reaction] || 1) - 1); }
    setComments((cs) => (cs || []).map((x) => (x.id === c.id ? { ...x, my_reaction: prev.my_reaction === key ? null : key, reactions: next } : x)));
    const queue = () => enqueueOffline({ kind: "comment_reaction", key: `comment:${c.id}`, method: "post", url: `/comments/${c.id}/react`, body: { type: key }, meta: { comment_id: c.id } });
    if (isOffline()) { queue(); return; }
    try {
      const { data } = await api.post(`/comments/${c.id}/react`, { type: key });
      setComments((cs) => (cs || []).map((x) => (x.id === c.id ? { ...x, my_reaction: data.my_reaction, reactions: data.reactions } : x)));
    } catch (e) {
      if (shouldQueue(e)) { queue(); return; }
      setComments((cs) => (cs || []).map((x) => (x.id === c.id ? { ...x, ...prev } : x)));
      toast.error(e?.friendlyMessage || "Erreur");
    }
  };

  const editComment = async (c, content) => {
    try {
      const { data } = await api.patch(`/comments/${c.id}`, { content });
      setComments((cs) => (cs || []).map((x) => (x.id === c.id ? { ...x, content: data.content, edited_at: data.edited_at } : x)));
      toast.success(t("comment.edited_toast", "Commentaire modifié"));
    } catch (e) { toast.error(e?.friendlyMessage || "Erreur"); }
  };

  const del = async (c) => {
    try {
      const { data } = await api.delete(`/comments/${c.id}`);
      setComments((cs) => (cs || []).filter((x) => x.id !== c.id && x.parent_id !== c.id));
      onCountChange?.(-(data.deleted || 1));
      emitPostPatch(post.id, { comment_delta: -(data.deleted || 1) });
      toast.success(t("comment.deleted", "Commentaire supprimé"));
    } catch (e) { toast.error(e?.friendlyMessage || "Erreur"); }
  };

  const toggleReplies = (id) => setExpandedReplies((e) => ({ ...e, [id]: !e[id] }));

  const isPostOwner = !!user && (user.id === post.author?.id || user.role === "super_admin" || user.role === "moderator");

  // Auto-expand the replies of a pinned comment so the best exchanges are surfaced.
  useEffect(() => {
    const pinned = (comments || []).find((c) => c.pinned && !c.parent_id);
    if (pinned && expandedReplies[pinned.id] === undefined && ((repliesOf[pinned.id] || []).length > 0)) {
      setExpandedReplies((e) => ({ ...e, [pinned.id]: true }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [comments]);

  const pinComment = async (c) => {
    const willPin = !c.pinned;
    setComments((cs) => (cs || []).map((x) => ({ ...x, pinned: x.id === c.id ? willPin : false })));
    try {
      if (willPin) await api.post(`/posts/${post.id}/comments/${c.id}/pin`);
      else await api.delete(`/posts/${post.id}/comments/${c.id}/pin`);
      toast.success(willPin ? t("comment.pinned_toast", "Commentaire épinglé 📌") : t("comment.unpinned_toast", "Commentaire désépinglé"));
    } catch (e) {
      setComments((cs) => (cs || []).map((x) => ({ ...x, pinned: x.id === c.id ? !willPin : x.pinned })));
      toast.error(e?.friendlyMessage || "Erreur");
    }
  };

  const startReply = (c) => {
    const targetId = c.parent_id || c.id;
    const nested = !!c.parent_id;
    setReplyTo({ id: targetId, name: c.author?.name, nested });
    if (nested && c.author?.id && c.author?.name) {
      setMentions((prev) => (prev.some((x) => x.id === c.author.id) ? prev : [...prev, { id: c.author.id, name: c.author.name }]));
      setText((prev) => (prev && prev.startsWith("@") ? prev : `@${c.author.name} `));
    }
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  const insertEmoji = (e) => { setText((prev) => prev + e); setEmojiOpen(false); setTimeout(() => inputRef.current?.focus(), 30); };

  const rowProps = { t, lang, user, reactions, onReact: react, onReply: startReply, onEdit: editComment, onDelete: del, onToggleReplies: toggleReplies, onPin: pinComment, onReport: setReportComment, isPostOwner, onOpenReactors: setReactorsCommentId, pulseId };

  const doReportComment = async (reason, category) => {
    if (!reportComment || reporting) return;
    setReporting(true);
    try {
      await api.post(`/comments/${reportComment.id}/report`, { reason, category });
      toast.success(
        category === "child_safety"
          ? t("report.csae_toast", "Signalement urgent transmis à notre équipe de sécurité. Merci de protéger notre communauté.")
          : t("comment.reported_toast", "Merci, ce commentaire a été signalé aux modérateurs")
      );
      setReportComment(null);
    } catch (e) { toast.error(e?.friendlyMessage || "Erreur"); }
    finally { setReporting(false); }
  };
  const typingNames = Object.values(typingUsers);
  const typingLabel = typingNames.length === 1
    ? t("comment.typing_one", "{name} est en train d'écrire…").replace("{name}", typingNames[0])
    : t("comment.typing_many", "Plusieurs personnes écrivent…");

  return (
    <>
      <div className={variant === "modal" ? "max-h-[66vh] overflow-y-auto" : ""} data-testid={`comment-scroll-${post.id}`}>
        {showPostFull && <PostFull post={post} onComment={() => inputRef.current?.focus()} />}

        <div
          ref={listRef}
          className={`px-4 py-4 space-y-4 ${showPostFull ? "border-t mt-2" : ""}`}
          style={{ borderColor: "var(--yalola-border)" }}
          data-testid={`comment-list-${post.id}`}
        >
          {comments === null && (
            <div className="py-10 grid place-items-center"><Loader2 className="animate-spin" style={{ color: "var(--yalola-primary)" }} /></div>
          )}
          {comments !== null && topLevel.length === 0 && (
            <div className="py-10 text-center text-sm flex flex-col items-center gap-2" style={{ color: "var(--yalola-text-muted)" }} data-testid={`comment-empty-${post.id}`}>
              <MessageCircle size={28} className="opacity-40" />
              {t("comment.empty", "Aucun commentaire pour l'instant. Sois le premier !")}
            </div>
          )}
          {topLevel.map((c) => {
            let replies = repliesOf[c.id] || [];
            const expanded = !!expandedReplies[c.id];
            let bestId = null;
            if (c.pinned && replies.length > 0) {
              replies = [...replies].sort((a, b) => reactTotal(b) - reactTotal(a));
              if (reactTotal(replies[0]) > 0) bestId = replies[0].id;
            }
            return (
              <div key={c.id}>
                <CommentRow c={c} isReply={false} replyCount={replies.length} expanded={expanded} {...rowProps} />
                {expanded && replies.map((r) => (<CommentRow key={r.id} c={r} isReply isBest={r.id === bestId} {...rowProps} />))}
              </div>
            );
          })}
        </div>
      </div>

      {typingNames.length > 0 && (
        <div className="px-4 pb-1 text-[12px] italic flex items-center gap-1.5" style={{ color: "var(--yalola-text-muted)" }} data-testid={`comment-typing-${post.id}`}>
          <span className="flex gap-0.5">
            <span className="h-1.5 w-1.5 rounded-full animate-bounce" style={{ background: "var(--yalola-primary)", animationDelay: "0ms" }} />
            <span className="h-1.5 w-1.5 rounded-full animate-bounce" style={{ background: "var(--yalola-primary)", animationDelay: "120ms" }} />
            <span className="h-1.5 w-1.5 rounded-full animate-bounce" style={{ background: "var(--yalola-primary)", animationDelay: "240ms" }} />
          </span>
          {typingLabel}
        </div>
      )}

      {user && (
        <div
          className={`border-t py-3 ${stickyComposer ? "sticky bottom-0 z-20 -mx-5 -mb-5 px-5" : "px-4"}`}
          style={stickyComposer
            ? { borderColor: "var(--yalola-border)", background: "var(--yalola-surface)", paddingBottom: "calc(var(--sa-bottom, 0px) + 12px)" }
            : { borderColor: "var(--yalola-border)" }}
        >
          {replyTo && (
            <div className="flex items-center justify-between text-xs mb-2 px-1" style={{ color: "var(--yalola-text-muted)" }} data-testid={`comment-replying-${post.id}`}>
              <span>{t("comment.replying_to", "En réponse à {name}").replace("{name}", replyTo.name || "")}</span>
              <button onClick={() => { setReplyTo(null); setText(""); setMentions([]); }} className="hover:text-red-600" data-testid={`comment-cancel-reply-${post.id}`}><X size={14} /></button>
            </div>
          )}
          <div className="flex gap-2 items-center relative">
            {mentionOpen && mentionResults.length > 0 && (
              <div className="absolute bottom-12 left-10 right-12 z-50 rounded-xl border bg-white shadow-lg overflow-hidden" style={{ borderColor: "var(--yalola-border)" }} data-testid={`comment-mention-list-${post.id}`}>
                {mentionResults.map((u) => (
                  <button key={u.id} onClick={() => pickMention(u)} className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-[color:var(--yalola-surface-alt)]" data-testid={`comment-mention-${u.id}`}>
                    <Avatar className="h-7 w-7"><AvatarImage src={avatarSrc(u)} /><AvatarFallback>{u.name?.[0]}</AvatarFallback></Avatar>
                    <span className="text-sm font-medium inline-flex items-center gap-1">{u.name} <VerifiedBadge user={u} size={11} /></span>
                  </button>
                ))}
              </div>
            )}
            <Avatar className="h-8 w-8"><AvatarImage src={avatarSrc(user)} /><AvatarFallback>{user.name?.[0]}</AvatarFallback></Avatar>
            <div className="flex-1 flex items-center gap-1 rounded-full border bg-white pr-1" style={{ borderColor: "var(--yalola-border)" }}>
              <input
                ref={inputRef}
                value={text}
                onChange={(e) => onChangeText(e.target.value)}
                onBlur={stopTyping}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                placeholder={replyTo ? t("comment.write_reply", "Écris une réponse…") : t("comment.write", "Écris un commentaire…")}
                className="flex-1 h-9 rounded-full px-3 text-base md:text-sm bg-transparent outline-none"
                data-testid={`comment-modal-input-${post.id}`}
              />
              <Popover open={emojiOpen} onOpenChange={setEmojiOpen}>
                <PopoverTrigger asChild>
                  <button type="button" className="h-8 w-8 grid place-items-center rounded-full hover:bg-[color:var(--yalola-surface-alt)]" {...keepFocusTap(() => setEmojiOpen((v) => !v), { withClick: false })} title={t("comment.add_emoji", "Ajouter un emoji")} data-testid={`comment-emoji-btn-${post.id}`}>
                    <Smile size={18} style={{ color: "var(--yalola-primary)" }} />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-60 p-2 z-[95]" align="end" side="top" collisionPadding={8} onOpenAutoFocus={(e) => e.preventDefault()} onCloseAutoFocus={(e) => e.preventDefault()} data-testid={`comment-emoji-panel-${post.id}`}>
                  <EmojiTabsGrid cols={6} cell="h-8 w-8 text-[22px]" onPick={insertEmoji} tapProps={(fn) => keepFocusTap(fn)} gridTestId={`comment-emoji-grid-${post.id}`} itemTestId={(e) => `comment-emoji-${e}-${post.id}`} />
                </PopoverContent>
              </Popover>
            </div>
            <Button size="sm" className="rounded-full pill-primary" {...keepFocusTap(send)} disabled={sending || !text.trim()} data-testid={`comment-modal-send-${post.id}`}>
              {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            </Button>
          </div>
        </div>
      )}

      <ReportDialog
        open={!!reportComment}
        onOpenChange={(v) => { if (!v) setReportComment(null); }}
        onReport={doReportComment}
        reporting={reporting}
        testidPrefix="comment-report"
        title={t("comment.report_title", "Signaler ce commentaire")}
      />
      <ReactorsDialog
        open={!!reactorsCommentId}
        onOpenChange={(v) => { if (!v) setReactorsCommentId(null); }}
        endpoint={reactorsCommentId ? `/comments/${reactorsCommentId}/reactions` : ""}
        testId="comment-reactors-dialog"
      />
    </>
  );
};

export default CommentThread;
